import React from 'react'
import { useTranslation } from 'react-i18next'
import { useTable, useSortBy, usePagination, useFilters } from 'react-table'
import { Input, Select } from 'components/form'
import { Link, FlexView } from 'components/common'
import styled from 'styled-components'
import { TableWrapper } from 'components/common/Table'

const Table = styled(TableWrapper)`
  display: flex;
  flex-direction: column;

  .table-wrap {
    flex: 1;
    overflow: auto;
  }

  th input {
    font-size: ${({ theme }) => theme.fontSizes.small};
    padding: 4px;
    min-height: 0px;
  }
`

const FilterableTableUpload = ({ columns, data, hasFooter, sortable, paginated, onRowClick, ...rest }) => {
    const { t } = useTranslation()
    const {
        footerGroups,
        getTableProps,
        getTableBodyProps,
        headerGroups,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        page,
        prepareRow,
        state: { pageIndex, pageSize },
    } = useTable(
        {
            columns,
            data,
            disableSortBy: !sortable
        },
        useFilters,
        useSortBy,
        usePagination
    )

    // Renderizar a UI da tabela
    return (
        <Table elevation="none" width="100%" fontSize="16px" {...rest}>
            <div className="table-wrap">
                <table {...getTableProps()}>
                    <thead>
                        {headerGroups.map(headerGroup => (
                            <tr {...headerGroup.getHeaderGroupProps()}>
                                {headerGroup.headers.map(column => (
                                    <th {...column.getHeaderProps()}>
                                        <div>{column.render('Header')}</div>
                                        {column.canFilter ? (
                                            <Input
                                                value={column.filterValue || ''}
                                                onChange={(e) => column.setFilter(e.target.value)}
                                                placeholder={`Filter ${column.Header}`}
                                            />
                                        ) : null}
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody {...getTableBodyProps()}>
                        {page.map(row => {
                            prepareRow(row)
                            return (
                                <tr {...(onRowClick ? row.getRowProps({ onClick: () => onRowClick(row) }) : row.getRowProps())}>
                                    {row.cells.map(cell => {
                                        return <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                                    })}
                                </tr>
                            )
                        })}
                    </tbody>
                    {hasFooter && (
                        <tfoot>
                            {footerGroups.map(group => (
                                <tr {...group.getFooterGroupProps()}>
                                    {group.headers.map(column => (
                                        <td {...column.getFooterProps()}>{column.render('Footer')}</td>
                                    ))}
                                </tr>
                            ))}
                        </tfoot>
                    )}
                </table>
            </div>
            {paginated && (
                <div className="pagination">
                    <FlexView flexDirection="row" justifyContent="flex-start" alignItems="center">
                        <Input
                            inline
                            type="number"
                            label={t('Go to page')}
                            fontSize="small"
                            margin="0px"
                            padding="8px 4px"
                            defaultValue={pageIndex + 1}
                            onChange={(e) => {
                                const page = e.target.value ? Number(e.target.value) - 1 : 0
                                gotoPage(page)
                            }}
                            width="180px"
                        />
                    </FlexView>
                    <FlexView flex="1" flexDirection="row" alignItems="center" justifyContent="center" width="100%">
                        <Link
                            noDecoration
                            color="gray"
                            fontSize="big"
                            fontWeight="bold"
                            padding="4px"
                            margin="0px 4px"
                            onClick={() => gotoPage(0)}
                            disabled={!canPreviousPage}
                        >
                            {'«'}
                        </Link>{' '}
                        <Link
                            noDecoration
                            color="gray"
                            fontSize="big"
                            fontWeight="bold"
                            padding="4px"
                            margin="0px 4px"
                            onClick={previousPage}
                            disabled={!canPreviousPage}
                        >
                            {'‹'}
                        </Link>{' '}
                        <FlexView flexDirection="row" margin="0px 8px" fontWeight="bold">
                            {pageIndex + 1} {t('of')} {pageOptions.length}
                        </FlexView>
                        <Link
                            noDecoration
                            color="gray"
                            fontSize="big"
                            fontWeight="bold"
                            padding="4px"
                            margin="0px 4px"
                            onClick={nextPage}
                            disabled={!canNextPage}
                        >
                            {'›'}
                        </Link>{' '}
                        <Link
                            noDecoration
                            color="gray"
                            fontSize="big"
                            fontWeight="bold"
                            padding="4px"
                            margin="0px 4px"
                            onClick={() => gotoPage(pageCount - 1)}
                            disabled={!canNextPage}
                        >
                            {'»'}
                        </Link>{' '}
                    </FlexView>
                    <FlexView flexDirection="row" justifyContent="flex-end" alignItems="center">
                        <Select
                            margin="0px"
                            value={pageSize}
                            options={[5, 10, 20, 30, 40, 50].map((pageSize) => ({
                                value: pageSize,
                                label: `${t('Show')} ${pageSize}`,
                            }))}
                            onChange={(value) => {
                                setPageSize(Number(value))
                            }}
                        />
                    </FlexView>
                </div>
            )}
        </Table>
    )
}

export default FilterableTableUpload
